import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import useSchoolAdminOrg from '../review/useSchoolAdminOrg';
import { AdminShellContext, activeMenu, adminSections } from './adminMenu';
import './SchoolAdminShell.css';

const LAST_REVIEW_KEY = 'wq-school-admin-last-review';

const readLastReview = () => {
  try {
    return window.localStorage.getItem(LAST_REVIEW_KEY) || null;
  } catch (_error) {
    return null;
  }
};

const ShellTabs = ({ sections, activeId }) => (
  <nav className="sadmin-tabs" aria-label="업무">
    {sections.map((item) => (
      <Link key={item.id} to={item.home} className={item.id === activeId ? 'is-active' : ''} aria-current={item.id === activeId ? 'page' : undefined}>
        {item.label}
      </Link>
    ))}
  </nav>
);

const ShellMenu = ({ section, activeId }) => (
  <aside className="sadmin-menu" aria-label={`${section.label} 메뉴`}>
    {section.groups.map((group) => (
      <div key={group.label} className="sadmin-menu-group">
        <p className="sadmin-menu-label">{group.label}</p>
        <ul>
          {group.items.map((item) => (
            <li key={item.id}>
              {item.href ? (
                <a href={item.href} target="_blank" rel="noopener noreferrer">{item.label}</a>
              ) : (
                <Link to={item.to} className={item.id === activeId ? 'is-active' : ''} aria-current={item.id === activeId ? 'page' : undefined}>
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </aside>
);

// 학교 관리 화면 전용 틀. 교사 개인 도구 사이드바 대신 업무 탭과 왼쪽 메뉴 트리로 업무 순서를 보여 준다.
const SchoolAdminShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, orgId, orgName, currentUser } = useSchoolAdminOrg();
  const showMenu = loading || Boolean(orgId);
  const active = activeMenu(location.pathname, location.search);

  useEffect(() => {
    if (!active.reviewId) return;
    try {
      window.localStorage.setItem(LAST_REVIEW_KEY, active.reviewId);
    } catch (_error) {
      // 저장소를 못 쓰면 다음에 검토 목록에서 다시 고르면 된다.
    }
  }, [active.reviewId]);

  const reviewId = active.reviewId || readLastReview();
  const sections = adminSections(reviewId);
  const section = sections.find((item) => item.id === active.section) || sections[0];
  const current = section.groups.flatMap((group) => group.items).find((item) => item.id === active.item);
  const view = active.section === 'review' || active.section === 'school' ? active.item : '';
  const { pathname, key: nonce } = location;
  const shell = useMemo(() => ({
    view,
    nonce,
    go: (next) => {
      if (next !== view) navigate(`${pathname}?view=${next}`, { replace: true });
    },
  }), [view, nonce, pathname, navigate]);
  const userName = currentUser?.displayName || currentUser?.email || '';
  const groupLabel = section.groups.find((group) => group.items.includes(current))?.label;
  const path = [section.label, groupLabel !== section.label ? groupLabel : null].filter(Boolean);

  return (
    <AdminShellContext.Provider value={shell}>
      <div className="sadmin">
        <header className="sadmin-bar">
          <Link className="sadmin-back" to="/home">← 왓퀴즈로 돌아가기</Link>
          <span className="sadmin-brand">왓퀴즈 학교관리</span>
          <span className="sadmin-who">
            {orgName && <strong>{orgName}</strong>}
            {userName && <span>{orgId ? `${userName} · 학교 관리자` : userName}</span>}
          </span>
        </header>
        {showMenu && <ShellTabs sections={sections} activeId={section.id} />}
        <div className={`sadmin-body${showMenu ? '' : ' is-plain'}`}>
          {showMenu && <ShellMenu section={section} activeId={current?.id} />}
          <div className="sadmin-content">
            {showMenu && current && (
              <p className="sadmin-path" aria-label="지금 위치">
                {path.join(' › ')} › <strong>{current.label}</strong>
              </p>
            )}
            {children}
          </div>
        </div>
      </div>
    </AdminShellContext.Provider>
  );
};

export default SchoolAdminShell;
